import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Heart, MessageCircle, Send, ArrowLeft, ShoppingBag, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function SetDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [set, setSet] = useState(null);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]); // hydrated for the Provenance drawer
  const [productsHydrated, setProductsHydrated] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const load = async (retry = 0) => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([api.get(`/sets/${id}`), api.get(`/sets/${id}/comments`)]);
      setSet(s.data); setComments(c.data); setLoading(false);
    } catch (e) {
      if (retry < 2) setTimeout(() => load(retry + 1), 600 * (retry + 1));
      else { setLoading(false); toast.error("Couldn't load this set"); }
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Hydrate product records lazily once the drawer opens. Each canvas item
  // already carries the data we need to *render* (image_url, category,
  // subcategory); the product fetch is purely to upgrade those rows with
  // title/price/source_url. If a product 404s (orphaned by a purge or
  // never seeded), we fall back to the item itself so the drawer is
  // never empty for a set that visibly has pieces.
  useEffect(() => {
    if (!drawerOpen || !set) return;
    if (productsHydrated) return;
    const itemsWithPid = (set.items || []).filter(it => it.product_id);
    if (itemsWithPid.length === 0) { setProducts([]); setProductsHydrated(true); return; }
    Promise.allSettled(
      itemsWithPid.map(it =>
        api.get(`/products/${it.product_id}`)
          .then(r => ({ ...r.data, _item: it }))
          .catch(() => ({
            // Fallback row from the canvas item itself
            id: it.product_id,
            title: it.title || "Untitled piece",
            image_url: it.image_url,
            price: it.price || "",
            brand: it.brand || "",
            source_url: it.source_url || "",
            category: it.category || "",
            subcategory: it.subcategory || "",
            _fallback: true,
            _item: it,
          }))
      )
    ).then((rs) => {
      const rows = rs
        .map(r => r.status === "fulfilled" ? r.value : null)
        .filter(Boolean);
      setProducts(rows);
      setProductsHydrated(true);
    });
  }, [drawerOpen, set, productsHydrated]);

  const toggleLike = async () => {
    if (!user) { toast.error("Login to like"); return; }
    const { data } = await api.post(`/sets/${id}/like`);
    setSet({ ...set, liked_by_me: data.liked, likes_count: set.likes_count + (data.liked ? 1 : -1) });
  };

  const post = async () => {
    if (!user) { toast.error("Login to comment"); return; }
    if (!text.trim()) return;
    const { data } = await api.post(`/sets/${id}/comments`, { text });
    setComments([...comments, data]);
    setText("");
    setSet({ ...set, comments_count: (set.comments_count || 0) + 1 });
  };

  if (loading || !set) {
    return <div className="min-h-[60vh] flex items-center justify-center font-mono text-xs uppercase tracking-widest">Loading…</div>;
  }

  // Count how many canvas items are linked to real products (vs. raw clips / backdrops)
  const sourceableCount = (set.items || []).filter(it => it.product_id).length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <Link to="/discover" className="font-mono text-xs uppercase tracking-widest inline-flex items-center gap-1 mb-6 hover:text-[#9CA47E]">
        <ArrowLeft className="w-3 h-3" /> Back to the journal
      </Link>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <div
            className="border border-[#E5E5E5] bg-white relative"
            style={(() => {
              const s = { aspectRatio: "1 / 1" };
              if (!set.backdrop_url) return s;
              if (set.backdrop_url.startsWith(":light/")) {
                const LIGHT_GRADIENTS = {
                  moody: "linear-gradient(135deg, #2F2A26 0%, #3D3934 60%, #5B524A 100%)",
                  linen: "linear-gradient(135deg, #F5F0E7 0%, #EFE7DA 60%, #E5DCC8 100%)",
                  amber: "linear-gradient(135deg, #C6916A 0%, #D9A77F 50%, #E9C39D 100%)",
                };
                const key = set.backdrop_url.slice(":light/".length);
                return { ...s, background: LIGHT_GRADIENTS[key] || "#FFFFFF" };
              }
              return {
                ...s,
                backgroundImage: `url(${resolveImageUrl(set.backdrop_url)})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              };
            })()}
            data-testid="set-canvas-view"
          >
            {set.items.map((it, i) => {
              const sx = it.flipped_x ? -1 : 1;
              const sy = it.flipped_y ? -1 : 1;
              return (
              <div
                key={i}
                className="absolute"
                style={{
                  left: `${(it.x / 800) * 100}%`,
                  top: `${(it.y / 800) * 100}%`,
                  width: `${(it.width / 800) * 100}%`,
                  height: `${(it.height / 800) * 100}%`,
                  zIndex: it.z_index,
                }}
              >
                <img
                  src={resolveImageUrl(it.image_url)}
                  alt=""
                  className="w-full h-full object-contain"
                  style={{ transform: `rotate(${it.rotation}deg) scale(${sx}, ${sy})` }}
                />
                {it.variant_author_username && (
                  <div
                    data-testid={`set-attribution-chip-${i}`}
                    title={`Edited by @${it.variant_author_username}${it.variant_kind ? ` · ${it.variant_kind}` : ""}`}
                    className="absolute -top-2 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-1.5 py-[2px] bg-white border border-[#9CA47E] font-mono text-[8px] uppercase tracking-[0.2em] whitespace-nowrap shadow-sm"
                  >
                    <span className="text-[#9CA47E]">◇</span>
                    <span className="text-[#333333]">@{it.variant_author_username}</span>
                  </div>
                )}
              </div>
            );})}
          </div>

          {/* Attached palette strip — only renders when the curator chose
              "Attach to composition" in the Studio. Renders an elegant 5-cell
              bar with HEX labels under the canvas. */}
          {set.palette_attached && Array.isArray(set.palette) && set.palette.length > 0 && (
            <div className="mt-4" data-testid="attached-palette">
              <div className="flex h-10 border border-[#E7E3DC]">
                {set.palette.map((c, i) => (
                  <div key={i} className="flex-1" style={{ backgroundColor: c }} title={c} />
                ))}
              </div>
              <div className="flex mt-1.5">
                {set.palette.map((c, i) => (
                  <div key={i} className="flex-1 text-center font-mono text-[10px] uppercase tracking-[0.22em] text-[#5C5C5C]">
                    {c}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* View Collection (Provenance) trigger — visible whenever the set
              has any items at all. Inside the drawer we show a graceful
              "atmospheric composition" empty state if none of those items
              carry a product_id. */}
          {(set.items || []).length > 0 && (
            <div className="mt-5">
              <button
                type="button"
                data-testid="open-collection-drawer"
                onClick={() => setDrawerOpen(true)}
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#9CA47E] hover:bg-[#7E876A] text-[#F2EDE3] font-mono text-[11px] uppercase tracking-[0.22em] transition-colors"
              >
                <ShoppingBag className="w-3.5 h-3.5" />
                {sourceableCount > 0
                  ? <>View collection · {sourceableCount} {sourceableCount === 1 ? "piece" : "pieces"}</>
                  : <>View composition</>}
              </button>
            </div>
          )}
        </div>

        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#9CA47E]">§ {set.category}</div>
          <h1 className="font-heading text-4xl mt-3" data-testid="set-title">{set.title}</h1>
          {set.description && <p className="font-body text-[#595959] mt-3 leading-relaxed">{set.description}</p>}

          <Link to={`/users/${set.author?.id}`} className="flex items-center gap-3 mt-6 group">
            <div className="w-10 h-10 bg-[#333333] text-white flex items-center justify-center font-mono text-sm">
              {set.author?.username?.[0]?.toUpperCase()}
            </div>
            <div>
              <div className="font-body text-sm group-hover:text-[#9CA47E]">@{set.author?.username}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#595959]">Curator</div>
            </div>
          </Link>

          {set.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">
              {set.tags.map((t) => <span key={t} className="px-3 py-1 text-[10px] uppercase tracking-widest border border-[#E5E5E5] bg-[#F2EDE3] font-mono">{t}</span>)}
            </div>
          )}

          <div className="flex gap-3 mt-8">
            <Button data-testid="like-set" onClick={toggleLike} variant={set.liked_by_me ? "default" : "outline"} className={`rounded-none ${set.liked_by_me ? "bg-[#9CA47E] hover:bg-[#7E876A] text-white" : "border-[#333333] text-[#333333]"}`}>
              <Heart className={`w-4 h-4 mr-2 ${set.liked_by_me ? "fill-current" : ""}`} /> {set.likes_count || 0}
            </Button>
            <Button variant="outline" className="rounded-none border-[#333333]">
              <MessageCircle className="w-4 h-4 mr-2" /> {set.comments_count || 0}
            </Button>
          </div>

          <div className="mt-10 border-t border-[#E5E5E5] pt-6">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#595959] mb-4">Comments ({comments.length})</div>
            <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
              {comments.length === 0 && <div className="text-sm font-body text-[#595959]">No comments yet — be the first.</div>}
              {comments.map((c) => (
                <div key={c.id} className="border-l-2 border-[#E5E5E5] pl-3" data-testid={`comment-${c.id}`}>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#333333]">@{c.author?.username || "anon"}</div>
                  <div className="font-body text-sm mt-1">{c.text}</div>
                </div>
              ))}
            </div>

            {user && (
              <div className="mt-4 flex gap-2">
                <input
                  data-testid="comment-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && post()}
                  placeholder="Add a comment…"
                  className="flex-1 border border-[#E5E5E5] focus:border-[#333333] focus:outline-none px-3 py-2 text-sm font-body"
                />
                <Button data-testid="comment-submit" onClick={post} className="rounded-none bg-[#333333]"><Send className="w-4 h-4" /></Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ---------- Collection (Provenance) Drawer ---------- */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex" data-testid="collection-drawer">
          <button
            type="button"
            aria-label="Close drawer"
            onClick={() => setDrawerOpen(false)}
            className="flex-1 bg-[#333333]/45 backdrop-blur-[2px] cursor-default"
            data-testid="drawer-backdrop"
          />
          <aside className="w-full sm:w-[420px] bg-[#F2EDE3] h-full overflow-y-auto shadow-2xl animate-in slide-in-from-right duration-300">
            <div className="sticky top-0 bg-[#F2EDE3] border-b border-[#E7E3DC] px-6 py-5 flex items-center justify-between z-10">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#9CA47E]">§ Provenance</div>
                <h2 className="font-heading text-2xl text-[#333333] mt-1.5 leading-tight">Inside the composition</h2>
              </div>
              <button
                type="button"
                data-testid="drawer-close"
                onClick={() => setDrawerOpen(false)}
                className="w-8 h-8 inline-flex items-center justify-center text-[#5C5C5C] hover:text-[#333333] hover:bg-[#CFC8B6] cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <p className="font-body text-[13px] text-[#5C5C5C] leading-relaxed">
                Every piece in this set, with credit and source. Tap a row to visit the original listing — no affiliate, no tracking, just where to find it.
              </p>

              {!productsHydrated ? (
                <div className="py-10 text-center font-mono text-[10px] uppercase tracking-[0.22em] text-[#5C5C5C]">
                  Hydrating items…
                </div>
              ) : products.length === 0 ? (
                <div className="py-10 text-center" data-testid="drawer-empty-state">
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#9CA47E] mb-2">◇ Atmospheric Composition</div>
                  <p className="font-body text-[13px] text-[#5C5C5C] leading-relaxed max-w-xs mx-auto">
                    This spread was composed purely as an atmosphere — no individual pieces to source. Save the mood, palette, and light for your own studio.
                  </p>
                </div>
              ) : (
                <ul className="space-y-3" data-testid="drawer-items">
                  {products.map((p) => (
                    <li key={p.id} data-testid={`drawer-item-${p.id}`} className="border border-[#E7E3DC] bg-white">
                      <a
                        href={p.source_url || "#"}
                        target={p.source_url ? "_blank" : undefined}
                        rel={p.source_url ? "noopener noreferrer" : undefined}
                        onClick={(e) => { if (!p.source_url) e.preventDefault(); }}
                        className="flex gap-3 p-3 group"
                      >
                        <div className="w-20 h-20 bg-[#EFEAE2] overflow-hidden shrink-0">
                          {p.image_url && (
                            <img src={resolveImageUrl(p.image_url)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col justify-between">
                          <div>
                            {p.brand && (
                              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#9CA47E] truncate">{p.brand}</div>
                            )}
                            <div className="font-heading text-base text-[#333333] leading-tight truncate" data-testid={`drawer-item-title-${p.id}`}>
                              {p.title}
                            </div>
                          </div>
                          <div className="flex items-center justify-between mt-1">
                            <span className="font-body text-sm text-[#333333]" data-testid={`drawer-item-price-${p.id}`}>
                              {p.price || <span className="text-[#A0998E] italic">price unlisted</span>}
                            </span>
                            {p.source_url ? (
                              <span data-testid={`drawer-item-source-${p.id}`} className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9CA47E] group-hover:text-[#7E876A]">
                                Go to source <ExternalLink className="w-3 h-3" />
                              </span>
                            ) : (
                              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A0998E]">no link</span>
                            )}
                          </div>
                        </div>
                      </a>
                    </li>
                  ))}
                </ul>
              )}

              {/* Palette echo inside the drawer — if attached */}
              {set.palette_attached && set.palette?.length > 0 && (
                <div className="pt-4 border-t border-[#E7E3DC]" data-testid="drawer-palette">
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#9CA47E] mb-2">§ Palette</div>
                  <div className="flex h-8">
                    {set.palette.map((c, i) => (
                      <div key={i} className="flex-1" style={{ backgroundColor: c }} title={c} />
                    ))}
                  </div>
                  <div className="flex mt-1">
                    {set.palette.map((c, i) => (
                      <div key={i} className="flex-1 text-center font-mono text-[9px] uppercase tracking-[0.2em] text-[#5C5C5C]">{c}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
